<script setup lang="ts">
  import { ref, computed, withDefaults, defineProps, onMounted, defineEmits, watch } from 'vue';
  import BcCheckbox from '@/ui-kit/components/BcCheckbox/BcCheckbox';
  import BcButton from '@/ui-kit/components/BcButton/BcButton';
  import BcModal from '@/ui-kit/components/BcModal';
  import ModalProcessAbortReason from '@/components/Modal/ModalProcess/ModalProcessAbortReason.vue';
  import ProcessDropdown from '@/components/Process/ProcessDropdown.vue';

  import { useProcessesStore } from '@/store/pinia/processes';
  import { useRouter } from 'vue-router/composables';
  import { useStore } from '@/store';
  import { useToast } from '@/ui-kit/components/BcToast';
  import { useElementBounding, useScroll } from '@vueuse/core';
  import { useI18n } from '@/i18n/i18n';

  import { Process } from '@/domains/models/Process';
  import { Opportunity } from '@/domains/models/Opportunity';
  import { ConfigurationProcessCategoryStep } from '@/domains/models/Organization';
  import { abortManyProcessCustom, unabortManyProcessCustom, updateProcessCustomStepAll } from '@/api/processes-custom';
  import { useProcessesFilter } from '@/composables/useProcessesFilter';

  interface Props {
    coderId?: string;
    customJobId?: string;
    customJob?: Opportunity;
    companyId?: string;
    isEmpty?: boolean;
    processesLength?: number;
    isMultiprocessAvailable?: boolean;
  }

  type ModalActionType = 'abort-process'

  type Step = { categoryKey: string; key: string; editable: boolean; name: string; email?: boolean; };

  const props = withDefaults(defineProps<Props>(), {
    coderId: undefined,
    customJobId: undefined,
    customJob: undefined,
    companyId: undefined,
    isEmpty: false,
    processesLength: 0,
    isMultiprocessAvailable: false,
  });

  const emit = defineEmits<{
    (e: 'update-processes', processes: Process[]): void;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const store = useStore();
  const toast = useToast();
  const processesStore = useProcessesStore();

  const {
    statusFilterOptions,
    processStatusFilter,
  } = useProcessesFilter({ withoutAll: true });

  const container = ref<HTMLDivElement | null>(null);
  const { y: containerScroll } = useScroll(container);

  const accordion = ref<HTMLDivElement | null>(null);
  const accordionBoundary = useElementBounding(accordion);

  const isCheckedAll = ref(false);
  const activeTabs = ref<number[]>([]);
  const modalState = ref<ModalActionType | null>(null);

  const organization = computed(() => {
    return store.state.user?.profile?._organization;
  });
  const configurationProcess = computed(() => {
    return store.getters['user/configurationProcess'];
  });

  const shouldSendMailOnReject = computed(() => organization?.value?.configuration?.multiposting?.sendEmailAfterApplicationTreated);
  const shouldSendMailOnFailure = computed(() => organization?.value?.configuration?.process?.failureEmail);

  const flatStatusOptions: ConfigurationProcessCategoryStep[] = organization.value.configuration.process?.categories?.reduce((acc, category) => {
    return [...acc, ...category.steps.map((step: Step) => ({
      ...step,
      categoryKey: category.key,
      value: step.key,
      label: step.name,
    }))];
  }, []);

  const selectedProcesses = computed(() => {
    return processesStore.selectedProcesses;
  });

  const selectedProcessesLength = computed(() => {
    return Object.keys(selectedProcesses.value).length;
  });

  const areFailedProcesses = computed(() => {
    if (!selectedProcessesLength.value) {
      return false
    }
    return Object.keys(selectedProcesses.value).every(processId => selectedProcesses.value[processId].abortedAt);
  });

  const areApplicationProcesses = computed(() => {
    if (!selectedProcessesLength.value) {
      return false
    }
    return Object.keys(selectedProcesses.value).every(processId => {
      const history = selectedProcesses.value[processId].historySteps.at(-1);
      return history?.stepKey === flatStatusOptions[0].key && !selectedProcesses.value[processId].abortedAt;
    });
  });

  const areActiveProcesses = computed(() => {
    if (!selectedProcessesLength.value) {
      return false
    }
    return Object.keys(selectedProcesses.value).every(processId => {
      const history = selectedProcesses.value[processId].historySteps.at(-1);
      return history?.stepKey !== flatStatusOptions[0].key && !selectedProcesses.value[processId].abortedAt;
    });
  });

  const handleApplications = () => {
    router.push({
      name: 'Applications',
      query: {
        ...props.coderId && { coderId: props.coderId },
        ...props.customJobId && { customJobIds: props.customJobId },
        ...props.companyId && { companyIds: props.companyId },
        forceRefreshApplications: 'true',
      },
    });
  }

  const handleReactivate = async () => {
    try {
      const { data } = await unabortManyProcessCustom(Object.keys(selectedProcesses.value));
      resetSelected();
      toast.show({
        title: 'Changement de statut',
        message: 'Les process sont bien réactivés',
        type: 'info',
        icon: 'info',
      });
      emit('update-processes', data);
    } catch (err: any) {
      toast.show({
        title: 'Erreur',
        message: err.message,
      });
    }
  }

  const handleAbort = async () => {
    if (configurationProcess.value.failureReason) {
      modalState.value = 'abort-process';
    } else {
      confirmAbortProcessesFromModal()
    }
  }

  const confirmAbortProcessesFromModal = async (params?: { reason: string }) => {
    if (shouldSendMailOnReject.value || shouldSendMailOnFailure.value) {
      await store.dispatch('emails/setEmailSentCallback', async() => {
        confirmAbortProcesses(params);
      });
      onSendMail({ isApplication: true, isMany: true });
    } else {
      await confirmAbortProcesses(params);
    }
  }

  const confirmAbortProcesses = async (params?: { reason: string }) => {
    try {
      const { data } = await abortManyProcessCustom({
        processIds: Object.keys(selectedProcesses.value),
        reason: params?.reason,
        rejected: true,
        sendEmail: false
      });
      resetSelected();
      toast.show({
        title: 'Changement de statut',
        message: 'Les process sont bien passés en "échec"',
        type: 'info',
        icon: 'info',
      });
      if (data?.processes?.length) {
        emit('update-processes', data.processes);
      }
      closeModal();
    } catch (err: any) {
      toast.show({
        title: 'Erreur',
        message: err.message,
      });
    }
  }

  const handleSteps = async (selectedStep: Step) => {
    if (selectedStep.email) {
      await store.dispatch('emails/setEmailSentCallback', async() => {
        comfirmStepsUpdate(selectedStep)
      });
      onSendMail({ isApplication: true, isMany: true });
    } else {
      await comfirmStepsUpdate(selectedStep);
    }
  }

  const comfirmStepsUpdate = async (selectedStep: Step) => {
    try {
      const { data } = await updateProcessCustomStepAll({
        processIds: Object.keys(selectedProcesses.value),
        categoryKey: selectedStep.categoryKey,
        stepKey: selectedStep.key,
      });
      resetSelected();
      toast.show({
        title: 'Changement de statut',
        message: `Les process sont bien passés en "${selectedStep.name}"`,
        type: 'info',
        icon: 'info',
      });
      if (data?.processes?.length) {
        emit('update-processes', data.processes);
      }
    } catch (err: any) {
      toast.show({
        title: 'Erreur',
        message: err.message,
      });
    }
  }

  const onCheckedAll = async (checked: boolean) => {
    try {
      if (checked) {
        const data = await processesStore.getProcessesCustom({
          coderId: props.coderId,
          customJobId: props.customJobId,
          companyId: props.companyId,
          onlyIds: true,
          stepKey: processStatusFilter.value.key,
        });

        if (data?.length) {
          processesStore.setAllSelectedProcess({ selectedProcesses: data });
        }
      } else {
        processesStore.removeAllSelectedProcess();
      }

      isCheckedAll.value = checked;
    } catch (err) {
      console.log('onCheckedAll err', err);
    }
  }

  const resetSelected = () => {
    processesStore.removeAllSelectedProcess();
    isCheckedAll.value = false;
  }

  const onSendMail = (params: any = {}) => {
    store.dispatch('app/setIsPanel', true);
    store.dispatch('emails/setSelectedCoders', Object.keys(selectedProcesses.value).map(processId => selectedProcesses.value[processId]?._coder));
    store.dispatch('app/setParams', {
      isCompany: false,
      ...props.customJob && { customJob: props.customJob },
      ...params,
    });
    store.dispatch('app/setModal', 'modal-email-send-form');
  }

  onMounted(() => {
    resetSelected();
  });

  const closeModal = () => {
    modalState.value = null;
    store.dispatch('app/setParams', {});
  };

  watch(() => processStatusFilter.value.key, () => {
    resetSelected();
  });
</script>

<template>
  <div ref="container" class="flex w-full flex-col">
    <div v-if="!isEmpty && isMultiprocessAvailable" class="my-4 pl-5">
      <BcCheckbox
        id="select_all"
        :value="isCheckedAll"
        @input="onCheckedAll"
      >
        <span class="text-sm">
          {{ t('process.select-all') }}
        </span>
      </BcCheckbox>
    </div>

    <div class="relative" :class="{ 'pb-16': selectedProcessesLength > 0 && processesLength >= 3 }">
      <slot></slot>
    </div>

    <div
      v-if="selectedProcessesLength"
      class="absolute bottom-0 z-50 ml--5 h-[130px] w-1/2 flex-col items-center justify-center pl-3"
      style="background: linear-gradient(0deg, white 70%, transparent);"
    >
      <div class="font-bold text-blue-800">
        {{ t('process.selection-of-applications', { number: selectedProcessesLength }) }}
      </div>
      <div class="mt-2 items-center gap-2">
        <BcButton v-if="areApplicationProcesses" iconLeft="file-cv" @click="handleApplications">
          {{ t('process.manage-my-applications') }}
        </BcButton>
        <BcButton v-if="areFailedProcesses" iconLeft="repeat" @click="handleReactivate">
          {{ t('process.reactivate') }}
        </BcButton>

        <ProcessDropdown
          v-if="areActiveProcesses"
          :active-tabs="activeTabs"
          :items="statusFilterOptions"
          :parent-height="accordionBoundary.height"
          :scroll-position="containerScroll"
          :value="processStatusFilter"
          as="button"
          size="extra-small"
          :selectedLabel="t('process.move-next-step')"
          @select="handleSteps"
        >
          <template #icon>
            <span class="icon-kanban mr-2 text-white"></span>
          </template>
        </ProcessDropdown>

        <BcButton v-if="areActiveProcesses" iconLeft="x-circle" color="error-base" @click="handleAbort">
          {{ t('generics.set-to-failed') }}
        </BcButton>

        <BcButton
          icon-left="mail-arrow-up"
          color="warning-gradient"
          @click="onSendMail"
        >
          <span>{{ $t('email.send-a-mail') }}</span>
        </BcButton>
        <BcButton type="outlined" @click="resetSelected">
          {{ t('generics.cancel') }}
        </BcButton>
      </div>
    </div>


    <BcModal :active="!!modalState" :display-close-button="false" @close="closeModal">
      <ModalProcessAbortReason
        v-if="configurationProcess.failureReason"
        :confirmLabel="t('process.validate-for-all')"
        :canSkip="true"
        @confirm-action="confirmAbortProcessesFromModal"
        @cancel-action="closeModal">
      </ModalProcessAbortReason>
    </BcModal>
  </div>
</template>
